/**
 * FirstEntryController — React port of the removed
 * `views/components/entries/FirstEntryLogic.ejs`.
 *
 * When a logged-in user opens one of their own graph pages
 * (`/:user/:context/edit`, `/:user/:context/google`, …) and the context has
 * no entries yet, this opens the import dialog that matches the app / mode
 * the page was opened in, so the user lands straight in the right "add your
 * first content" flow. On non-empty graphs it shows the SEO / niche helper
 * messages and persists the default version for chat / AI-idea graphs.
 * (The context-vs-user language mismatch warning that used to live here
 * moved to `components/LanguageWarnings`.)
 *
 * If the user closes the auto-opened dialog while the graph is still
 * empty, a centered GraphEmptyState overlay stays on screen with an
 * "import data" action that reopens the same dialog (LazyGraph hides its
 * own copy of the overlay while this flow is active).
 *
 * Differences from the EJS original (intentional):
 *  - Dialogs are opened through `DialogsContext` directly instead of
 *    emitting a guarded `openImportDialog` event. The old inline script ran
 *    before React registered its listener, so its synchronous emits were a
 *    lost race; this runs after mount and always lands.
 *  - The redundant `getJsonPath()` re-fetch (its own TODO said unnecessary)
 *    is dropped — `userFactory.getGraphEmpty()` is server-rendered from the
 *    same entries list.
 *  - The `#advanced_google` / `#advanced_twitter` / `#close-firstentry*` /
 *    `#interpret` DOM wiring is dropped: those nodes live in
 *    `FirstEntry.ejs`, which is not included on any page.
 *  - Runs only for the graph owner (`!isPublic`). The EJS was included for
 *    any session user, so a logged-in visitor on someone ELSE's empty
 *    public graph also got an import dialog — dropped on purpose.
 */
import * as React from 'react'

import { DialogsContext } from '../../DialogsContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'
import { useZustand } from '../../hooks/useZustand'
import { useGraphStore } from '../../stores/useGraphStore'
import GraphEmptyState from '../../home/GraphEmptyState'
import { getCurrentContext } from '../LeftMenuMigration/legacyBridge'
import {
    isGraphEmptyOnLoad,
    getCurrentApp,
    getGraphOption,
    hideAnalyticsPanel,
    forceHideAnalyticsPanel,
    forceOpenStatements,
    showHelperTop,
    saveDefaultGraphVersion,
} from './legacyBridge'

const NICHE_HELPER_MESSAGE = `This graph shows the current informational supply vs demand. These are the most prominent keyword combinations (not just keywords) people use to find information related to your query that don't tend to appear in the top search results for that query. <strong>Target those to maximize interest in your ideas.</strong> Use the filter menu at the top left of the page to view demand and supply separately.`

const SEO_HELPER_MESSAGE = `This graph shows the most prominent keyword combinations (not just keywords) people use when they make the search query you provided that do not yet appear in the top search results for that query. <strong>Target those to maximize informational gain.</strong> <br><br>Click "Reveal Missing Nodes" in the Analytics > Missing Content panel to only see the missing keywords. Use the filter menu at the top left of the page to view the graphs of related search queries and SERP separately.`

const HELPER_MESSAGE_TIMEOUT = 120000

// Empty-graph dialog per app (the first if/else chain of the EJS original).
// 'twitter_social' is intentionally absent: it only hid the analytics pane.
const EMPTY_GRAPH_DIALOG_BY_APP = {
    seo: 'seo',
    kwrds: 'keywords',
    niche: 'niche',
    google: 'google',
    notes: 'textImport',
    surveys: 'textImport',
    reviews: 'textImport',
    twitter: 'twitter',
    science: 'science',
}

// Empty-graph dialog per `?mode=` for graphs without a dedicated app path.
const EMPTY_GRAPH_DIALOG_BY_MODE = {
    insight_ai_idea: 'aiIdea',
    SCIENCE: 'science',
    explore: 'aiTopics',
    overview: 'aiTopics',
    gpt3: 'aiChat',
}

/**
 * Once the context's statements are known, persist the default graph
 * version for just-seeded chat / AI-idea graphs (≤1 statement). Mirrors the
 * EJS `statementsLoaded` listener, but reads the zustand store instead so
 * it can't miss an event that fired before this component mounted.
 * Returns an unsubscribe/cancel function.
 */
function saveDefaultVersionOnFirstStatements(buildVersionUrlParams) {
    let done = false
    let unsubscribe = null

    const evaluate = () => {
        if (done) return
        const {
            loadedStatements,
            statements,
            currentContext,
        } = useZustand.getState()
        if (!loadedStatements || !currentContext) return
        done = true
        if (unsubscribe) unsubscribe()
        const statementCount = (statements[currentContext] || []).length
        if (statementCount > 1) return
        saveDefaultGraphVersion(buildVersionUrlParams())
    }

    evaluate()
    if (!done) unsubscribe = useZustand.subscribe(evaluate)

    return () => {
        done = true
        if (unsubscribe) unsubscribe()
    }
}

export default function FirstEntryController() {
    const {
        activeImportDialog,
        setActiveImportDialog,
        setPresetContext,
        setPresetSearchString,
        setBackLinkDialog,
        firstEntryDialog,
        setFirstEntryDialog,
    } = React.useContext(DialogsContext)
    const isPublic = useContextsStore(selectIsPublicView)
    const { location } = usePageInfo()
    const hasRun = React.useRef(false)

    // Track graph content so the reopen overlay disappears as soon as the
    // user actually adds something (same emptiness check as LazyGraph).
    const isGraphLoaded = useGraphStore(state => state.isGraphLoaded)
    const graphNodeCount = useGraphStore(
        state =>
            state.graphData?.entriesAndGraphOfContext?.graph?.graphologyGraph
                ?.nodes?.length || 0
    )

    // Same payload Home's 'openImportDialog' event handler applies, plus
    // remembering the dialog so the empty-state overlay can reopen it.
    const openDialog = React.useCallback(
        dialogName => {
            setActiveImportDialog(dialogName)
            setPresetContext(getCurrentContext() || '')
            setPresetSearchString('')
            setBackLinkDialog('backLink')
            setFirstEntryDialog(dialogName)
        },
        [
            setActiveImportDialog,
            setPresetContext,
            setPresetSearchString,
            setBackLinkDialog,
            setFirstEntryDialog,
        ]
    )

    React.useEffect(() => {
        if (hasRun.current) return
        if (location !== 'context') return
        if (isPublic) return
        if (typeof window === 'undefined') return
        hasRun.current = true

        // The legacy statementsPanel() shim was already a thin wrapper over
        // this same zustand field — use the store directly.
        const isStatementsPanelOpen = () => !!useZustand.getState().panelOpen
        const setStatementsPanelOpen = open =>
            useZustand.getState().updatePanelOpen(open)

        const app = getCurrentApp()
        const mode = getGraphOption('mode')
        const isEmpty = isGraphEmptyOnLoad()
        // `?force=` keeps showing the Google-app import dialog over a
        // non-empty graph (used by "search again" links).
        const forceImport = !!new URLSearchParams(window.location.search).get(
            'force'
        )

        let cleanup

        // `typeof … === 'string'` doubles as a hasOwnProperty guard, so an
        // unexpected app/mode segment (e.g. 'toString') can't reach a
        // prototype member through the lookup.
        const appDialog =
            typeof EMPTY_GRAPH_DIALOG_BY_APP[app] === 'string'
                ? EMPTY_GRAPH_DIALOG_BY_APP[app]
                : null
        const modeDialog =
            typeof EMPTY_GRAPH_DIALOG_BY_MODE[mode] === 'string'
                ? EMPTY_GRAPH_DIALOG_BY_MODE[mode]
                : null

        if (appDialog && (isEmpty || (app === 'google' && forceImport))) {
            // App pages get their import dialog and a softly hidden
            // analytics pane (no statements-panel changes).
            hideAnalyticsPanel()
            openDialog(appDialog)
        } else if (app === 'twitter_social' && isEmpty) {
            hideAnalyticsPanel()
        } else if (isEmpty) {
            if (app === 'mindmap') {
                if (!isStatementsPanelOpen()) setStatementsPanelOpen(true)
                openDialog('mindmapImport')
            } else if (modeDialog) {
                openDialog(modeDialog)
            } else if (getGraphOption('speech')) {
                // Voice-input graphs ("Speech to graph" card, the /apps Voice
                // chip): no import dialog — open the add form with voice input
                // on the source the URL asks for (`?source=`, `?mic=0`).
                const params = new URLSearchParams(window.location.search)
                const source = ['mic', 'tab', 'screen'].includes(
                    params.get('source')
                )
                    ? params.get('source')
                    : 'mic'
                useZustand.getState().requestAddStatement()
                useZustand.getState().requestVoiceInput({
                    source,
                    includeMic: params.get('mic') !== '0',
                })
            } else if (
                app !== 'findall' &&
                !getGraphOption('chat') &&
                !getGraphOption('editor')
            ) {
                openDialog('import')
                if (isStatementsPanelOpen()) setStatementsPanelOpen(false)
            } else if (
                isStatementsPanelOpen() &&
                app !== 'mindmap' &&
                !getGraphOption('editor')
            ) {
                setStatementsPanelOpen(false)
            }

            forceOpenStatements()
            forceHideAnalyticsPanel()
        } else if (app === 'niche') {
            showHelperTop(
                NICHE_HELPER_MESSAGE,
                HELPER_MESSAGE_TIMEOUT,
                'niche_helper'
            )
        } else if (app === 'seo') {
            showHelperTop(
                SEO_HELPER_MESSAGE,
                HELPER_MESSAGE_TIMEOUT,
                'seo_helper'
            )
        } else if (getGraphOption('chat')) {
            if (mode === 'insight_chat') {
                cleanup = saveDefaultVersionOnFirstStatements(
                    () =>
                        `context=${getCurrentContext()}&version_name=chat&version_default=1&saveurl=chat%3D${getGraphOption(
                            'chat'
                        )}&mode=insight_chat`
                )
            }
        } else if (mode === 'insight_ai_idea') {
            // The persisted default-version URL is whatever sits in the
            // `saveurl` value — the route (`lib/context.js`) stores ONLY
            // `req.body.saveurl` as the revisionUrl; anything after it as a
            // separate `&`-param is dropped. So `mode=insight_ai_idea` must
            // live INSIDE the saveurl value, not trail it.
            cleanup = saveDefaultVersionOnFirstStatements(
                () =>
                    `context=${getCurrentContext()}&version_name=chat&version_default=1&saveurl=mode%3Dinsight_ai_idea`
            )
        }

        return cleanup
    }, [location, isPublic, openDialog])

    // After the user closes the auto-opened import dialog with the graph
    // still empty, keep a centered hint on screen that can reopen it.
    // LazyGraph suppresses its own GraphEmptyState while `firstEntryDialog`
    // is set, so only this copy (with the import link) shows.
    const isGraphStillEmpty = !isGraphLoaded || graphNodeCount === 0
    const showReopenHint =
        Boolean(firstEntryDialog) && !activeImportDialog && isGraphStillEmpty

    if (!showReopenHint) return null

    return <GraphEmptyState onImportClick={() => openDialog(firstEntryDialog)} />
}
